import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import type { McpServer } from "@/app/mcp-gateways/types";
import { onboardingProgressQueryOptions } from "@/app/onboarding/api/get-onboarding-progress";
import { onboardingKeys } from "@/app/onboarding/api/query-keys";
import { patchProgressAfterGatewayCreate } from "@/app/onboarding/lib/patch-progress-after-gateway-create";
import { useOrgProjectRouteContext } from "@/lib/org-project-context/hooks";
import { useResourceScope } from "@/lib/resource-context";

/**
 * Reconcile onboarding progress for the current org/project.
 *
 * - No args: mark stale and refetch active observers (tool finish, dismiss).
 * - With gateway: patch jtbd → connect from the create response, then
 *   `fetchQuery` so the UI can advance before closing (onboarding create flows).
 */
export function useReconcileOnboardingProgress() {
  const queryClient = useQueryClient();
  const scope = useResourceScope();
  const { organizationId, projectId } = useOrgProjectRouteContext();

  return useCallback(
    async (created?: McpServer) => {
      if (created) {
        patchProgressAfterGatewayCreate(queryClient, scope, created);
        await queryClient.fetchQuery(
          onboardingProgressQueryOptions(organizationId, projectId)
        );
        return;
      }

      await queryClient.invalidateQueries({
        queryKey: onboardingKeys.progress({ scope }),
      });
    },
    [queryClient, organizationId, projectId, scope]
  );
}
